import type { ImageLoaderProps } from 'next/image'

// Normalize and encode src for Cloudflare CDN.
// Strips the leading slash and URL-encodes so query strings
// (e.g. `?v=123`, signed S3 URLs) survive the rewrite.
function normalizeSrc(src: string): string {
  const normalized = src.startsWith('/') ? src.slice(1) : src
  return encodeURIComponent(normalized)
}

/**
 * Custom image loader for Cloudflare Workers.
 * Rewrites to Cloudflare's CDN image transformation endpoint (`/cdn-cgi/image/`),
 * which is edge-terminated and never reaches the Worker.
 *
 * In development: returns the src with the requested params appended so the
 * browser fetches the origin image directly.
 * In production: routes through `/cdn-cgi/image/` for format/size/quality
 * negotiation (WebP, AVIF, PNG, JPEG, GIF).
 *
 * @see https://opennext.js.org/cloudflare/howtos/image
 * @see https://developers.cloudflare.com/images/image-resizing/
 */
export default function cloudflareLoader({
  src,
  width,
  quality,
}: ImageLoaderProps): string {
  const params = [`width=${width}`]

  if (quality) {
    params.push(`quality=${quality}`)
  }

  if (process.env.NODE_ENV === 'development') {
    const separator = src.includes('?') ? '&' : '?'
    return `${src}${separator}${params.join('&')}`
  }

  return `/cdn-cgi/image/${params.join(',')}/${normalizeSrc(src)}`
}
